// ══════════════════════════════════════════════════════════════════════════
//  BUSCAR CON CRITERIO: un solo motor para toda la app (2026-09-18)
//
//  El dueño, al buscar "proyecto de taludes" y ver presupuestos que no lo
//  dicen: "no entiendo cómo se están indexando las búsquedas cuando algo tan
//  preciso no presenta resultados, no está pero sigue mostrando lo parecido";
//  y con "cabo real" salían los de Cabo San Lucas porque un concepto decía
//  "realizar". Antes cada palabra valía como pedazo de texto en cualquier parte
//  del registro, y la lista no distinguía lo que coincidía de lo que se parecía.
//
//  Ahora:
//   · Una palabra coincide donde EMPIEZA una palabra ("real" encuentra "real" y
//     "reales", no "irreal"); dentro de otra palabra solo cuenta como parecido.
//   · Las palabras vacías ("de", "la", "y"...) no se exigen solas, pero sí
//     cuentan dentro de la frase.
//   · Tres niveles, en este orden:
//       0  la frase entera aparece tal cual en un campo;
//       1  todas las palabras, completas (o en plural), aparecen juntas en un
//          mismo campo (un nombre, un concepto, una nota), en cualquier orden;
//       2  parecido: las palabras están, pero repartidas o dentro de otras.
//   · Entre comillas ("cabo real") solo vale la frase exacta.
//   · La lista dice la verdad con las MISMAS palabras en toda la app
//     (textosBusqueda): "Coinciden con «X»", "Con todas las palabras",
//     "Parecidos" y, si nada la dice, "Nada coincide con «X»".
//
//  Puro, sin React; con vitest. utils.coincide y utils.plegar salen de aquí.
// ══════════════════════════════════════════════════════════════════════════

// Pliega para comparar: minúsculas y sin diacríticos ("José" = "jose").
const DIACRITICOS = /[̀-ͯ]/g;
export const plegar = (s) => String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(DIACRITICOS, '');

// Palabras que no se exigen solas: casi todo documento las lleva y exigirlas no
// separa nada. Dentro de la frase sí cuentan ("proyecto de taludes").
const VACIAS = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'lo', 'y', 'e', 'o', 'u', 'a', 'al', 'en',
  'con', 'por', 'para', 'un', 'una', 'unos', 'unas', 'que', 'se', 'su', 'sus']);

// La puntuación pegada a los bordes ("jose," "tules.") no es parte del dato;
// dentro del término se respeta ("14/08/2026", "f-26249", "$53,673.20", "15%").
const BORDES = /^[^\p{L}\p{N}$]+|[^\p{L}\p{N}%]+$/gu;
const COMILLAS = /^\s*["“”«„'](.*?)["“”»']?\s*$/s;

// La consulta como se entiende: `frase` (todas las palabras, en orden),
// `terminos` (las que se exigen), `exacta` (venía entre comillas) y `texto` (lo
// tecleado, para decirlo de vuelta en la lista).
export function consultaDe(q) {
  const crudo = String(q == null ? '' : q);
  const m = COMILLAS.exec(crudo);
  const exacta = !!m && /^\s*["“”«„']/.test(crudo) && crudo.trim().length > 1;
  const dentro = exacta ? m[1] : crudo;
  const palabras = plegar(dentro).split(/\s+/).map((t) => t.replace(BORDES, '')).filter(Boolean);
  const llenas = palabras.filter((t) => !VACIAS.has(t));
  return {
    texto: dentro.trim(),
    frase: palabras.join(' '),
    terminos: llenas.length ? llenas : palabras,
    exacta,
    vacia: palabras.length === 0,
  };
}

const LETRA = /[\p{L}\p{N}]/u;
// ¿`termino` aparece en `texto` (ambos plegados) donde empieza una palabra?
export function alInicioDePalabra(texto, termino) {
  if (!termino) return true;
  let i = texto.indexOf(termino);
  while (i >= 0) {
    if (i === 0 || !LETRA.test(texto[i - 1])) return true;
    i = texto.indexOf(termino, i + 1);
  }
  return false;
}

// ¿`termino` aparece como palabra COMPLETA (o su plural: "proyecto" encuentra
// "proyectos", "taludes" también "talud" no)? "real" no es "realizar".
export function palabraCompleta(texto, termino) {
  if (!termino) return true;
  let i = texto.indexOf(termino);
  while (i >= 0) {
    if (i === 0 || !LETRA.test(texto[i - 1])) {
      const resto = texto.slice(i + termino.length, i + termino.length + 3);
      const fin = (k) => k >= resto.length || !LETRA.test(resto[k]);
      if (fin(0) || (resto[0] === 's' && fin(1)) || (resto.startsWith('es') && fin(2))) return true;
    }
    i = texto.indexOf(termino, i + 1);
  }
  return false;
}

// La frase aparece tal cual, empezando donde empieza una palabra. Los espacios
// del campo se toman como uno ("cabo  real" también dice "cabo real").
const diceLaFrase = (campo, frase) => alInicioDePalabra(campo.replace(/\s+/g, ' '), frase);

// El nivel de un registro para una consulta: 0 dice la frase, 1 tiene todas las
// palabras juntas en un campo, 2 se parece, null no coincide. `campos` ya
// plegados (se pliegan una vez al indexar, no en cada tecleo).
export function nivelDe(consulta, campos) {
  if (!consulta || consulta.vacia) return 0;
  const { frase, terminos, exacta } = consulta;
  if (campos.some((c) => diceLaFrase(c, frase))) return 0;
  if (exacta) return null;
  // Juntas: cada palabra COMPLETA (o su plural) en un mismo campo. La frase
  // acepta la última palabra a medio escribir; aquí no, o "cabo real" juntaría
  // "Cabo San Lucas, realizar sondeos".
  if (terminos.length > 1 && campos.some((c) => terminos.every((t) => palabraCompleta(c, t)))) return 1;
  const todo = campos.join('\n');
  return terminos.every((t) => todo.includes(t)) ? 2 : null;
}

// Atajo para quien tiene la consulta cruda y los campos sin plegar.
export const nivel = (q, ...campos) => nivelDe(consultaDe(q), campos.map(plegar));

// ¿Coincide, en cualquier nivel? Lo que usan los filtros de la app (antes,
// utils.coincide). Sin consulta, todo coincide.
export const coincide = (q, ...campos) => nivel(q, ...campos) != null;

// ── Las palabras de la búsqueda, iguales en toda la app ────────────────────
export function textosBusqueda(texto) {
  const q = String(texto || '').trim();
  return {
    exactas: `Coinciden con «${q}»`,
    juntas: 'Con todas las palabras',
    parecidos: 'Parecidos',
    nada: `Nada coincide con «${q}»`,
  };
}

// Ordena por nivel (lo que coincide primero) sin perder el orden de la lista
// dentro de cada nivel. `camposDe(item)` devuelve los textos del registro.
// Cada resultado lleva `_nivel`. Sin consulta devuelve la lista tal cual.
export function buscarEn(items, q, camposDe) {
  const consulta = consultaDe(q);
  if (consulta.vacia) return items;
  const res = [];
  items.forEach((it, orden) => {
    const n = nivelDe(consulta, (camposDe(it) || []).map(plegar));
    if (n != null) res.push({ it, n, orden });
  });
  res.sort((a, b) => a.n - b.n || a.orden - b.orden);
  return res.map(({ it, n }) => (it && typeof it === 'object' ? { ...it, _nivel: n } : it));
}

// Los grupos de una lista mientras se busca, con las palabras de siempre:
//  · todo coincide con la frase: una lista lisa, sin rótulos;
//  · mezcla de niveles: un grupo por nivel, rotulado;
//  · nada dice la frase: primero "Nada coincide con «X»" y luego lo que hay.
// `items` ya pasados por buscarEn (llevan `_nivel`). Forma de grupo de la
// carpeta: { clave, etiqueta, items, sinConteo }.
export function gruposDeBusqueda(items, q, nivelDeItem = (it) => it._nivel ?? 0) {
  const consulta = consultaDe(q);
  const t = textosBusqueda(consulta.texto);
  const por = [[], [], []];
  items.forEach((it) => { por[Math.min(2, Math.max(0, nivelDeItem(it)))].push(it); });
  const [cero, uno, dos] = por;
  if (!uno.length && !dos.length) return [{ clave: 'buscar-0', etiqueta: null, items: cero }];
  const grupos = [];
  if (cero.length) grupos.push({ clave: 'buscar-0', etiqueta: t.exactas, items: cero });
  else grupos.push({ clave: 'buscar-nada', etiqueta: t.nada, items: [], sinConteo: true });
  if (uno.length) grupos.push({ clave: 'buscar-1', etiqueta: t.juntas, items: uno });
  if (dos.length) grupos.push({ clave: 'buscar-2', etiqueta: t.parecidos, items: dos });
  return grupos;
}
